import React, { useCallback, useMemo } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { fieldControlStyle } from './field-control-style';

type ColorFieldProps = {
  field: { label?: string };
  name: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

/** Expands a 3-digit hex shorthand (#rgb) to a 6-digit hex (#rrggbb). */
function expandShortHex(value: string): string {
  return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
}

/** Extracts the 6-digit hex and alpha (0-1) from a color value (#rrggbb or #rrggbbaa). */
function parseColor(value: string): { hex: string; alpha: number } {
  if (!value) return { hex: '#000000', alpha: 1 };

  if (value.startsWith('#') && value.length === 9) {
    const hex = value.slice(0, 7);
    const alpha = parseInt(value.slice(7, 9), 16) / 255;
    return { hex, alpha: Math.round(alpha * 100) / 100 };
  }

  if (value.startsWith('#') && value.length === 7) {
    return { hex: value, alpha: 1 };
  }

  if (value.startsWith('#') && value.length === 4) {
    return { hex: expandShortHex(value), alpha: 1 };
  }

  return { hex: '#000000', alpha: 1 };
}

/** Combines a 6-digit hex and alpha (0-1) into the stored color value. */
function toColorValue(hex: string, alpha: number): string {
  if (alpha >= 1) return hex;
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${alphaHex}`;
}

/** Puck custom field widget: hex color input with opacity slider. */
export function ColorField({ field, name, value, onChange, readOnly }: ColorFieldProps): JSX.Element {
  const { hex, alpha } = useMemo(() => parseColor(value), [value]);

  const handleColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(toColorValue(e.currentTarget.value, alpha)),
    [onChange, alpha]
  );

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.value), [
    onChange,
  ]);

  const handleAlphaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newAlpha = parseFloat(e.currentTarget.value);
      onChange(toColorValue(hex, newAlpha));
    },
    [onChange, hex]
  );

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type='color'
          value={hex}
          onChange={handleColorChange}
          disabled={readOnly}
          data-testid={`color-swatch-${name}`}
          style={{ width: 32, height: 32, padding: 0, border: '1px solid #d0d4d8', borderRadius: 4, cursor: 'pointer' }}
        />
        <input
          type='text'
          value={value}
          onChange={handleTextChange}
          disabled={readOnly}
          data-testid={`color-text-${name}`}
          style={{
            ...fieldControlStyle,
            flex: 1,
            minWidth: 0,
            fontFamily: 'monospace',
          }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <input
          type='range'
          min='0'
          max='1'
          step='0.01'
          value={alpha}
          onChange={handleAlphaChange}
          disabled={readOnly}
          data-testid={`color-alpha-${name}`}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: 12, color: '#646d72', minWidth: 36, textAlign: 'right' }}>
          {Math.round(alpha * 100)}%
        </span>
      </div>
    </FieldLabel>
  );
}
